import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
  Input,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { AbilityServiceSignal } from '@casl/angular';
import { TranslateModule } from '@ngx-translate/core';
import { Store } from '@ngxs/store';
import { AuthorizationModule } from '@wilson/authorization';
import { FeatureFlagPurePipe } from '@wilson/feature-flags';
import { FilterService } from '@wilson/filter';
import {
  RoleAction,
  RolePermissionSubject,
  TimelineShowActivitiesQueryParamKey,
  ZOOM_LEVEL,
} from '@wilson/interfaces';
import {
  DragAndDropModeService,
  TabEnum,
} from '@wilson/shift-timeline/services';
import {
  ClearDeterminedShiftActivities,
  ClearShiftActivitiesRows,
  SetAreActivitiesVisible,
  SetSelectedUnassignedRegion,
  ShiftTimelineSettingsState,
  ShiftTimelineUnassignedRegionState,
} from '@wilson/shift-timeline/state';
import { NzSwitchModule } from 'ng-zorro-antd/switch';
import { NzTooltipModule } from 'ng-zorro-antd/tooltip';
import { Subject, Subscription, combineLatest, debounceTime, map } from 'rxjs';

@Component({
  selector: 'wilson-timeline-render-activities-toggle',
  templateUrl: './timeline-render-activities-toggle.component.html',
  styleUrls: ['./timeline-render-activities-toggle.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    FormsModule,
    TranslateModule,
    NzSwitchModule,
    AuthorizationModule,
    NzTooltipModule,
  ],
})
export class TimelineRenderActivitiesToggleComponent
  implements OnInit, OnDestroy
{
  private readonly store = inject(Store);
  private readonly filterService = inject(FilterService);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly dragAndDropModeService = inject(DragAndDropModeService);
  private readonly featureFlagPurePipe = inject(FeatureFlagPurePipe);
  private cdr = inject(ChangeDetectorRef);

  @Input() isCellView = false;

  RoleAction = RoleAction;
  RolePermissionSubject = RolePermissionSubject;
  protected can = inject(AbilityServiceSignal).can;
  protected disabledActivityToggleOnLargerZoomLevel = false;

  private toggleActivityVisibilitySubject = new Subject<boolean>();
  private subscriptions = new Subscription();

  areActivitiesVisible$ = this.store.select(
    ShiftTimelineSettingsState.areActivitiesVisible,
  );

  ngOnInit(): void {
    const areActivitiesVisibleQueryValue = this.activatedRoute.snapshot
      .queryParams[TimelineShowActivitiesQueryParamKey] as string;
    if (areActivitiesVisibleQueryValue) {
      const areActivitiesVisible =
        areActivitiesVisibleQueryValue?.toLowerCase() === 'true';
      this.saveToStateAndQueryParam(areActivitiesVisible);
    } else {
      const areActivitiesVisible = this.store.selectSnapshot(
        ShiftTimelineSettingsState.areActivitiesVisible,
      );

      this.saveToStateAndQueryParam(!!areActivitiesVisible);
    }

    this.subscriptions.add(
      this.toggleActivityVisibilitySubject
        .pipe(debounceTime(300))
        .subscribe((activityVisibility) => {
          this.saveToStateAndQueryParam(activityVisibility);
        }),
    );

    this.subscriptions.add(
      combineLatest([
        this.store.select(ShiftTimelineSettingsState.zoomLevel),
        this.featureFlagPurePipe.transform(
          'portal-timeline-activities-off-on-big-zooms',
        ),
      ])
        .pipe(
          map(([zoomLevel, featureFlagValue]) => {
            const isMonthZoomLevel =
              zoomLevel === ZOOM_LEVEL.HALF_MONTH ||
              zoomLevel === ZOOM_LEVEL.MONTH;

            if (featureFlagValue && isMonthZoomLevel) {
              this.toggleActivityVisibilitySubject.next(false);
              this.disabledActivityToggleOnLargerZoomLevel = true;
            } else {
              this.disabledActivityToggleOnLargerZoomLevel = false;
            }
            this.cdr.detectChanges();
          }),
        )
        .subscribe(),
    );
  }

  toggleActivityVisibility(activityVisibility: boolean): void {
    const updatedActivityVisibility = !activityVisibility;
    this.toggleActivityVisibilitySubject.next(updatedActivityVisibility);
  }

  private saveToStateAndQueryParam(areActivitiesVisible: boolean): void {
    const isVisible =
      !this.isCellView &&
      !this.disabledActivityToggleOnLargerZoomLevel &&
      areActivitiesVisible;
    this.store.dispatch(new SetAreActivitiesVisible(isVisible));
    if (!isVisible) {
      this.store.dispatch([
        new ClearDeterminedShiftActivities(),
        new ClearShiftActivitiesRows(),
      ]);
    }
    this.filterService
      .saveValueInQueryParam(
        TimelineShowActivitiesQueryParamKey,
        isVisible.toString(),
      )
      .then(() => this.toggleUnassignedRegionTabByVisibility(isVisible));
  }

  private toggleUnassignedRegionTabByVisibility(
    areActivitiesVisible: boolean,
  ): void {
    const selectedRegion = this.store.selectSnapshot(
      ShiftTimelineUnassignedRegionState.selectedRegion,
    );
    if (
      selectedRegion === TabEnum.UnassignedActivities &&
      !areActivitiesVisible
    ) {
      this.store.dispatch(
        new SetSelectedUnassignedRegion(TabEnum.UnassignedShifts),
      );
      this.dragAndDropModeService.setUnassignedRegionInUrl(
        TabEnum.UnassignedShifts,
      );
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
